import { useMemo, useState } from "react";
import { Button, Col, Label, Row } from "reactstrap";
import Select from "react-select";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import PageContainer from "@components/layout/PageContainer";
import ModelIndex from "@components/layout/ModelIndex";
import { renderEDocumentStatusOffCanvasTrigger } from "@utils/ModelIndexUtils";
import {
  EDOCUMENT_REPORT_ALL_KINDS,
  EDOCUMENT_REPORT_KIND_LABELS,
  buildEDocumentReportFilter,
  formatEDocumentDate,
  formatEDocumentTotal,
  resolveEDocumentKindLabel,
  resolveEDocumentViewUrl,
} from "@utils/EDocumentReport";
import { EDocumentReportRow } from "@/formTypes/EDocumentReportDataType";
import { isDiagnosableModel } from "@/api/EDocumentDiagnosticApi";
import { EDocumentStatusReference } from "@components/layout/Offcanvas/EDocumentStatusOffCanvas";
import styles from "./style.module.scss";

const ALL_KINDS_OPTION = { value: EDOCUMENT_REPORT_ALL_KINDS, label: "Todos los documentos" };

/**
 * El reporte mezcla los documentos que este sistema emite con los mensajes de aceptacion o
 * rechazo de comprobantes ajenos (AtvMessage), que no tienen lineas que diagnosticar.
 */
const resolveDiagnosticReference = (row: EDocumentReportRow): EDocumentStatusReference | null =>
  isDiagnosableModel(row.sourceModel) && row.docId
    ? { model: row.sourceModel, id: `${row.docId}` }
    : null;

const KIND_OPTIONS = [
  ALL_KINDS_OPTION,
  ...Object.entries(EDOCUMENT_REPORT_KIND_LABELS).map(([value, label]) => ({ value, label })),
];

/**
 * Listado unico de todos los comprobantes electronicos emitidos, del mas nuevo al mas
 * antiguo. Se alimenta del indice unificado `edocuments` de Meilisearch porque cada tipo
 * de documento vive en un modelo distinto (Invoice, Creditnote, Debitnote, BuyInvoice y
 * los mensajes de aceptacion/rechazo en AtvMessage).
 */
const EDocumentsReport = () => {
  const [kind, setKind] = useState<string>(EDOCUMENT_REPORT_ALL_KINDS);

  const filter = useMemo(() => buildEDocumentReportFilter(kind), [kind]);
  const selectedOption = KIND_OPTIONS.find((o) => o.value === kind) || ALL_KINDS_OPTION;

  const openExcelExport = () => {
    window.open(`/user/reports/excel/edocuments/${kind}`, "_blank");
  };

  return (
    <PageContainer
      title="Reporte de Documentos Electronicos"
      backUrl="/user/dashboard-accounting"
      primaryActions={[
        {
          actionTitle: "Exportar a Excel",
          customElement: (
            <Button
              data-testid="edocument-export-excel"
              color="success"
              onClick={openExcelExport}
            >
              Exportar a Excel
            </Button>
          ),
        },
      ]}
    >
      <Row className={styles.filterRow}>
        <Col md="4">
          <Label for="edocument-kind-filter">Tipo de documento</Label>
          <div data-testid="edocument-kind-filter">
            <Select
              inputId="edocument-kind-filter"
              name="kind"
              value={selectedOption}
              options={KIND_OPTIONS}
              onChange={(newValue) => setKind(`${newValue?.value || EDOCUMENT_REPORT_ALL_KINDS}`)}
            />
          </div>
        </Col>
      </Row>
      <p className={styles.notice}>
        Se listan todos los comprobantes electronicos, del mas reciente al mas antiguo. La columna
        Estado ATV muestra la respuesta de Hacienda; al hacer clic se abre el detalle del rechazo.
      </p>
      <ModelIndex<EDocumentReportRow>
        messages={{
          noResults: "No hay documentos electronicos para el filtro seleccionado",
          noFilteredResults: "No hay documentos electronicos que coincidan con la busqueda",
        }}
        meiliConfig={{
          index: "edocuments",
          sort: ["fechaTs:desc"],
          filter,
          hitsPerPage: 30,
        }}
        primaryProperties={[
          {
            key: "sequence",
            highlighted: true,
            className: styles.sequence,
          },
          {
            key: (data) => `${data.nombre || ""}`,
            alignment: "Start",
          },
        ]}
        primaryPropertiesSizing={[
          { sm: "5", xs: "12" },
          { sm: "7", xs: "12" },
        ]}
        secondaryProperties={[
          {
            // `data-fecha-ts` deja que un E2E verifique el orden sin parsear el texto.
            key: (data) => (
              <span data-testid="edocument-fecha" data-fecha-ts={`${data.fechaTs || 0}`}>
                {formatEDocumentDate(data.fecha)}
              </span>
            ),
            title: "Fecha",
          },
          {
            // El testid lleva el tipo para que un E2E pueda afirmar que el filtro aplico.
            key: (data) => (
              <span data-testid={`edocument-row-${data.kind || "sin-tipo"}`}>
                {resolveEDocumentKindLabel(data.kind)}
              </span>
            ),
            title: "Tipo",
          },
          {
            key: (data) => renderEDocumentStatusOffCanvasTrigger(data, resolveDiagnosticReference(data)),
            title: "Estado ATV",
          },
          {
            key: (data) => formatEDocumentTotal(data),
            title: "Total",
            highlighted: true,
          },
        ]}
        secondaryPropertiesSizing={[
          { sm: "4", md: "2" },
          { sm: "4", md: "3" },
          { sm: "4", md: "3" },
          { sm: "4", md: "2" },
        ]}
        listActions={[
          {
            actionTitle: "Ver Documento",
            iconProps: { icon: faEye },
            isVisible: (item) => !!resolveEDocumentViewUrl(item),
            // La ruta depende del tipo de documento, y `href` solo admite una URL fija
            // con el placeholder {:id}.
            onClick: (_id, item) => window.open(resolveEDocumentViewUrl(item), "_blank"),
          },
        ]}
      />
    </PageContainer>
  );
};

export default EDocumentsReport;
